// Khung chung mọi màn chưa đăng nhập (Đăng nhập, Đăng ký, Quên / Đặt lại mật khẩu, Nhập mã, Nhận lời mời, Đổi mật khẩu
// bắt buộc — styles/login.css). Luôn màu + logo ShopTrack: NPP không đổi giao diện các màn này (thương hiệu NPP chỉ áp
// sau khi đăng nhập). Máy tính: một thẻ kính lớn giữa màn (logo nằm trong thẻ) trên nền cực quang, trang trí bằng vòng
// tròn đồng tâm quay chậm + ô kính biểu tượng (không chữ), dòng đơn vị chủ quản dưới thẻ. Điện thoại: dải màu trên cùng có logo +
// bản đồ tuyến chìm (RouteMap), khung trắng kéo từ dưới lên, không chuyển động. Trang quản trị (apps/admin)
// có bản chép cùng kiểu.

import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Bike, MapPin, Package, ReceiptText, ScanBarcode, Store, Truck, Wallet, type LucideIcon } from 'lucide-react'
import { SHOPTRACK_SLOGAN, ShopTrackLogo } from '@/components/ui/logo'
import { RouteMap } from './RouteMap'
import '@/styles/login.css'

const BASE = (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')

export function AuthStage({ children, homeLink }: { children: ReactNode; /** bấm logo về trang này (vd. "/") */ homeLink?: string }) {
  const linked = (node: ReactNode, className: string) =>
    homeLink ? <Link to={homeLink} className={className} aria-label="ShopTrack">{node}</Link> : node
  return (
    <div className="lg-root">
      <AuthDecor />
      {/* điện thoại: dải màu có logo */}
      <aside className="lg-hero">
        <RouteMap className="lg-map" />
        <header className="lg-brand">
          {linked(
            <>
              <ShopTrackLogo brandable={false} showText={false} className="cursor-default text-white" imgClassName="lg-mark max-h-none" />
              <span className="lg-brand-text">
                <span className="lg-brand-name">ShopTrack</span>
                <span className="lg-brand-slogan">{SHOPTRACK_SLOGAN}</span>
              </span>
            </>,
            'lg-brand-link',
          )}
        </header>
      </aside>
      <main className="lg-sheet">
        <div className="lg-panel">
          {/* máy tính: logo trong thẻ */}
          <div className="lg-card-brand">
            {linked(
              <>
                <span className="lg-app-icon">
                  <ShopTrackLogo brandable={false} showText={false} className="cursor-default text-white" imgClassName="lg-app-mark max-h-none" />
                </span>
                <span className="lg-card-name">Shop<span>Track</span></span>
                <span className="lg-card-slogan">{SHOPTRACK_SLOGAN}</span>
              </>,
              'lg-card-link',
            )}
          </div>
          {children}
        </div>
        <footer className="lg-foot">
          <p className="lg-org">
            <img src={`${BASE}pthub-logo.png`} alt="PTHub" width={78} height={20} draggable={false} />
            <span>Sản phẩm của Công ty Cổ phần PTHub</span>
          </p>
        </footer>
      </main>
    </div>
  )
}

// Trang trí nền máy tính: 3 quỹ đạo đồng tâm quanh thẻ quay chậm (vòng trong / ngoài cùng chiều kim đồng hồ, vòng giữa
// ngược lại), trên mỗi quỹ đạo có ô kính biểu tượng việc giao hàng (luôn đứng thẳng) và chấm sáng.
// Ô: [biểu tượng, cỡ px, góc độ lúc đầu (0 = phải, theo chiều kim đồng hồ), nghiêng độ]
type Tile = [LucideIcon, number, number, number]
const ORBITS: Array<{ r: number; sec: number; ccw?: boolean; far?: boolean; dots: number[]; tiles: Tile[] }> = [
  { r: 420, sec: 180, dots: [205, 343], tiles: [[Package, 76, 162, 6], [Wallet, 72, 14, -6]] },
  { r: 580, sec: 240, ccw: true, dots: [160, 15], tiles: [[Truck, 96, 196, -8], [Store, 88, 335, 7], [ScanBarcode, 60, 40, 5]] },
  { r: 760, sec: 300, far: true, dots: [180, 60], tiles: [[MapPin, 60, 151, -5], [Bike, 52, 207, 7], [ReceiptText, 48, 326, -7]] },
]
const css = (v: Record<string, string | number>) => v as CSSProperties

function AuthDecor() {
  return (
    <div className="lg-decor" aria-hidden>
      {ORBITS.map(({ r, sec, ccw, far, dots, tiles }) => (
        <div key={r} className="lg-orbit" data-ccw={ccw || undefined} data-far={far || undefined} style={css({ '--r': `${r}px`, '--sec': `${sec}s` })}>
          {dots.map((deg) => (
            <span key={deg} className="lg-sat" style={css({ '--a': `${deg}deg` })}><span className="lg-dot" /></span>
          ))}
          {tiles.map(([Icon, size, deg, tilt]) => (
            <span key={deg} className="lg-sat" style={css({ '--a': `${deg}deg` })}>
              <span className="lg-tile" style={css({ '--s': `${size}px`, '--t': `${tilt}deg` })}><Icon strokeWidth={1.75} /></span>
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}

/** Tiêu đề khung: một dòng, kèm dòng phụ khi là dữ liệu (vd. email nhận mã). */
export function AuthTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="lg-head">
      <h1 className="lg-title">{children}</h1>
      {sub && <p className="lg-sub">{sub}</p>}
    </div>
  )
}
